import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useState } from 'react';
import {
  RadioGroup, Radio, FormControlLabel, TextField, Avatar, Checkbox,
  Divider, Tooltip,
} from '@mui/material';
import { NETWORK_IDS, USER_TYPES } from 'lib/constants';
import { useApi } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { toNumber, isEmpty } from 'lodash';
import { NetworkUser, User } from 'lib/types/users';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { NetworkIcon } from 'elements/NetworkIcon';
import { RoleSelect } from 'components/RoleSelect';
import { LocationAutocomplete } from 'components/LocationAutocomplete/LocationAutocomplete';
import { RolesProvider } from 'lib/contexts/rolesContext';
import { Role, Location } from 'lib/types';
import { GeocodeFeature } from '@mapbox/mapbox-sdk/services/geocoding';
import { mapboxContextToRegions } from 'lib/helpers';
import { AvatarImageUpload } from 'components/AvatarImageUpload';

interface Props {
  onClose: () => void;
  onSave: (user: User) => void;
  username?: string;
}

interface SocialOptions {
  twitter: NetworkUser;
  instagram: NetworkUser;
  tiktok: NetworkUser;
  existingUser: NetworkUser;
  twitch: NetworkUser;
  youtube: NetworkUser;
  facebook: NetworkUser;
  threads: NetworkUser;
  snapchat: NetworkUser;
}

const NETWORK_ORDER = [
  NETWORK_IDS.instagram,
  NETWORK_IDS.tiktok,
  NETWORK_IDS.twitch,
  NETWORK_IDS.threads,
  NETWORK_IDS.twitter,
  NETWORK_IDS.youtube,
  NETWORK_IDS.facebook,
  NETWORK_IDS.snapchat,
] as const;

const USERNAME_REGEX = /^[\w.]+$/i;

const initialUser = {
  userTypeId: USER_TYPES.creator.id,
};

interface UserToCreate extends NetworkUser {
  userTypeId: number;
  defaultRoleId?: number;
}

export const AddNewCreator = ({
  onClose, onSave, username,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [newUser, setNewUser] = useState<Partial<UserToCreate>>(initialUser);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(username);
  const [postingAccounts, setPostingAccounts] = useState<NetworkUser[]>([]);
  const [location, setLocation] = useState<Location | undefined>(undefined);
  const [defaultRole, setDefaultRole] = useState<Role | undefined>(undefined);

  const [options, setOptions] = useState<SocialOptions>();
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [skipUsername, setSkipUsername] = useState(false);

  const notFound = (result: SocialOptions) => {
    if (!result) return true;

    return isEmpty(result.existingUser) && isEmpty(result.instagram)
      && isEmpty(result.tiktok) && isEmpty(result.twitter) && isEmpty(result.youtube)
      && isEmpty(result.facebook) && isEmpty(result.threads);
  };

  const search = async () => {
    setOptions(undefined);
    if (!searchTerm || !USERNAME_REGEX.test(searchTerm)) {
      enqueueSnackbar('Please provide a valid username', {
        variant: 'error',
      });
      return;
    }
    try {
      setIsSearching(true);
      const results = await API.lookUpAllNetworks(searchTerm, true);
      setOptions(results);
    } catch (err) {
      enqueueSnackbar(`Something went wrong: ${err}`, {
        variant: 'error',
      });
    }
    setIsSearching(false);
  };

  const updateLocation = (
    locationString?: string,
    geocodeFeature?: Partial<GeocodeFeature>,
  ) => {
    if (!locationString && !geocodeFeature) {
      setLocation(undefined);
      return;
    }

    if (geocodeFeature) {
      // Handle GeocodeFeature input
      try {
        let x = 0;
        let y = 0;
        if (geocodeFeature.center?.length && geocodeFeature.center.length > 0) {
          [x, y] = geocodeFeature.center;
        }
        const locationData = {
          placeId: geocodeFeature.id || '',
          x,
          y,
          name: geocodeFeature.place_name || '',
          regions: mapboxContextToRegions(geocodeFeature.context),
        };
        setLocation(locationData);
      } catch (error) {
        // If geocodeFeature processing fails, clear location
        setLocation(undefined);
      }
    } else if (locationString) {
      try {
        if (locationString.trim().length === 0) {
          setLocation(undefined);
          return;
        }

        setLocation({
          placeId: '',
          x: 0,
          y: 0,
          name: locationString,
          regions: [],
        });
      } catch (error) {
        setLocation(undefined);
      }
    }
  };

  const updateDefaultAccount = (account?: NetworkUser, clearUsername = false) => {
    setNewUser({ ...newUser, ...account });
    if (account?.location) {
      updateLocation(account.location);
    }
    if (clearUsername) {
      setNewUser((prev) => ({ ...prev, username: undefined }));
    }
  };

  const changeLocation = async (_location: Partial<GeocodeFeature> | null) => {
    updateLocation(undefined, _location || undefined);
  };

  const getOptions = () => {
    if (!options) {
      return null;
    }
    if (notFound(options)) {
      return (
        <div>
          <b>
            @
            {searchTerm}
          </b>
          {' '}
          wasn&apos;t found on any platforms.
          <br />
          You can still manually add them without socials.
        </div>
      );
    }
    if (!isEmpty(options.existingUser)) {
      return (
        <div>
          A user with that username already exists:
          {socialOption(options.existingUser, 0)}
        </div>
      );
    }

    return (
      <>
        {NETWORK_ORDER.map((networkId) => {
          const networkKey = Object.keys(NETWORK_IDS).find(
            (key) => NETWORK_IDS[key as keyof typeof NETWORK_IDS] === networkId,
          ) as keyof SocialOptions;
          return socialOption(options[networkKey], networkId);
        })}
      </>
    );
  };

  const handleSelect = (option: NetworkUser) => {
    setPostingAccounts((prev) => {
      const newAccounts = [...prev, option];
      // Set as default account if it's the first one selected
      if (prev.length === 0) {
        updateDefaultAccount(option);
      }
      return newAccounts;
    });
  };

  const handleUnselect = (option: NetworkUser) => {
    setPostingAccounts((prev) => {
      const newAccounts = prev.filter((account) => (
        account.username !== option.username || account.network !== option.network
      ));

      // If we're removing the default account, set the next one as default
      if (newUser.username === option.username
        && newUser.network === option.network) {
        const nextDefault = newAccounts[0];
        if (nextDefault) {
          updateDefaultAccount(nextDefault);
        } else {
          updateDefaultAccount(undefined, true);
        }
      }

      return newAccounts;
    });
  };

  const isAccountSelected = (option: NetworkUser) => (
    postingAccounts.some((account) => (
      account.username === option.username && account.network === option.network
    ))
  );

  const isDefaultAccount = (option: NetworkUser) => (
    newUser
    && newUser.username === option.username
    && newUser.network === option.network
  );

  const handleChangeDefaultAccount = (option: NetworkUser) => {
    if (isDefaultAccount(option)) {
      updateDefaultAccount(undefined);
      return;
    }
    updateDefaultAccount(option);
  };

  const socialOption = (option: NetworkUser, networkId: number) => {
    if (isEmpty(option)) {
      return null;
    }

    const isSelected = isAccountSelected(option);
    const isDefault = isDefaultAccount(option);

    return (
      <div
        key={`${option.username}-${networkId}`}
        className="flex items-center gap-4 py-2 px-3 rounded-lg transition-colors"
      >
        <Checkbox
          checked={isSelected}
          onChange={() => {
            if (isSelected) {
              handleUnselect(option);
            } else {
              handleSelect(option);
            }
          }}
          disabled={networkId === 0 || !!option.id}
        />
        {networkId === 0 ? (
          <img alt="logo" src="/red-logo.png" width={24} height={24} />
        ) : (
          <NetworkIcon network={networkId} iconColor="color" />
        )}
        <Avatar
          sx={{ width: 48, height: 48 }}
          alt={option.username}
          src={option.avatarUrl && imageAvatarUrl(option.avatarUrl)}
        />
        <div className="text-sm">
          <div>{option.name}</div>
          <div className="font-semibold">
            @
            {option.username}
          </div>
          <div className="flex space-x-2 text-xs text-gray-500">
            <span>
              <PeopleIcon fontSize="small" style={{ marginRight: '5px' }} />
              {option.followersCount?.toLocaleString() || '--'}
            </span>
            {option.location && (
              <span>
                |
                <LocationOnIcon fontSize="small" style={{ margin: '0 3px' }} />
                {option.location}
              </span>
            )}
          </div>
        </div>
        <div className="ml-auto">
          <Tooltip
            title={isDefault ? 'This is the default account for this user.' : 'Set as default account'}
            placement="top"
            arrow
          >
            <button
              type="button"
              onClick={() => handleChangeDefaultAccount(option)}
              disabled={networkId === 0}
            >
              {isDefault ? <StarIcon color="primary" /> : <StarBorderIcon />}
            </button>
          </Tooltip>
        </div>
      </div>
    );
  };

  const save = async () => {
    setIsSaving(true);

    const userToCreate = {
      ...newUser,
      defaultRoleId: defaultRole?.id,
      location,
    };

    try {
      const createdUser = await API.createUser(userToCreate);
      await API.addPostingAccounts(
        postingAccounts.map((acc) => ({
          ...acc,
          userId: createdUser.id,
          network: acc.network,
        })),
        createdUser.id,
      );
      enqueueSnackbar('User created successfully!', { variant: 'success' });
      onSave(createdUser);
    } catch (error) {
      enqueueSnackbar(`Error creating user: ${error}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const saveAvatar = async (fileData: FormData, filename: string) => {
    const response = await API.uploadMedia(fileData, filename, `userAvatars/gondola/${newUser.username}`, undefined, undefined, false);
    setNewUser((prev) => ({ ...prev, avatarUrl: response[0].imageUrl }));
  };

  return (
    <Dialog open maxWidth="xl" fullWidth>
      <div className="p-6">
        <div className="absolute right-2 top-2">
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <h1 className="text-xl font-semibold mb-2">Add a new user to Gondola</h1>
        {/* Step one: username */}
        <div className="mb-4 grid columns-4">
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            InputProps={{
              startAdornment: '@',
            }}
            label="username"
            placeholder="Search TikTok, Instagram, YouTube, Facebook, Twitch and Twitter handles"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full col-span-4 md:col-span-3"
          />
          <div className="col-span-4 md:col-span-1 mt-4">
            <button
              disabled={!searchTerm || isSearching}
              type="button"
              className="btn-primary mr-2"
              onClick={search}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            <button
              disabled={!!postingAccounts.length}
              type="button"
              className="btn-primary"
              onClick={() => setSkipUsername(true)}
            >
              Skip username
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Step two: social options */}
          <div className="w-1/2 md:col-span-1">
            <div className="font-semibold mb-2">
              {options && !notFound(options) && 'Select an account'}
            </div>
            {isSearching ? (
              <div className="w-full text-center">Searching...</div>
            ) : (
              <div className="flex flex-col space-y-2">
                {getOptions()}
                {(options && !options.existingUser && !postingAccounts.length) && (
                  <button type="button" className="btn-primary mt-4" onClick={() => setNewUser({ username: searchTerm })}>
                    Create a user without socials
                  </button>
                )}
              </div>
            )}
          </div>

          {(newUser.username || skipUsername) && (
            <div className="md:col-span-1">
              <Divider orientation="vertical" flexItem />

              {/* Step Three: additional details */}
              <div className="space-y-4">
                <div className="font-semibold mb-2">User preview</div>
                {(newUser.username || skipUsername) && (
                  <>
                    <div className="grid md:grid-cols-2 items-center gap-4 px-3">
                      <div className="w-1/2">
                        <AvatarImageUpload
                          avatarUrl={newUser.avatarUrl ? imageAvatarUrl(newUser.avatarUrl) : ''}
                          username={newUser.username || ''}
                          onUpload={saveAvatar}
                        />
                      </div>
                      <div className="text-sm w-full">
                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          label="Name"
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        />
                        <div className="font-semibold">
                          {skipUsername ? 'No username provided' : (
                            <span>
                              @
                              {newUser.username}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      variant="standard"
                      label="Blurb"
                      value={newUser.blurb}
                      onChange={(e) => setNewUser({ ...newUser, blurb: e.target.value })}
                    />
                  </>
                )}
                <RadioGroup
                  aria-labelledby="type-radio-group"
                  name="type-radio-group"
                  value={newUser.userTypeId || USER_TYPES.creator.id}
                  onChange={(e) => setNewUser({ ...newUser, userTypeId: toNumber(e.target.value) })}
                >
                  <div className="font-semibold mt-2">This account is for a...</div>
                  <div className="flex">
                    <FormControlLabel
                      key={USER_TYPES.creator.key}
                      value={USER_TYPES.creator.id}
                      control={<Radio />}
                      label={<b>{USER_TYPES.creator.key}</b>}
                    />
                    <FormControlLabel
                      key={USER_TYPES.company.key}
                      value={USER_TYPES.company.id}
                      control={<Radio />}
                      label={<b>{USER_TYPES.company.key}</b>}
                    />
                  </div>
                </RadioGroup>
                <RolesProvider>
                  <RoleSelect
                    onChange={(value) => setDefaultRole(value as Role)}
                    value={defaultRole || null}
                    label="Default Role"
                    excludePhasedOut
                  />
                </RolesProvider>
                <LocationAutocomplete
                  onChange={(value) => changeLocation(value)}
                  label="Location"
                  defaultValue={{ place_name: location?.name }}
                  locationTypes={['place', 'locality', 'neighborhood', 'country', 'region']}
                />
                <div className="font-semibold mb-2">Posting Accounts</div>
                {postingAccounts.map((acc) => (
                  <div
                    key={`${acc.username}-${acc.network}`}
                    className="flex items-center gap-2 p-2"
                  >
                    <NetworkIcon network={acc.network} iconColor="color" />
                    <Avatar
                      sx={{ width: 40, height: 40 }}
                      alt={acc.username}
                      src={acc.avatarUrl && imageAvatarUrl(acc.avatarUrl)}
                    />
                    <div className="text-sm">
                      <div>
                        {acc.name}
                      </div>
                      <div className="font-semibold">
                        @
                        {acc.username}
                      </div>
                      <div className="flex space-x-2 text-xs text-gray-500">
                        <span>
                          <PeopleIcon fontSize="small" style={{ marginRight: '5px' }} />
                          {acc.followersCount?.toLocaleString() || '--'}
                        </span>
                        {acc.location && (
                          <span>
                            |
                            <LocationOnIcon fontSize="small" style={{ margin: '0 3px' }} />
                            {acc.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            className="btn-primary-outlined"
            onClick={onClose}
            type="button"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={isSearching || isSaving || (!newUser.username?.trim() && !skipUsername)}
            onClick={save}
            type="button"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
